import { useEffect, useRef, useState, useLayoutEffect } from "react";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import { Link, useParams } from "react-router-dom";
import { LuDownload } from "react-icons/lu";
import documentImg from "../../../assets/images/Blue and Peach Gradient Facebook Profile Picture.png";

const PAGE_SIZE = 20;

function OtherMessagesView() {
  const { id: conversationId } = useParams();

  const containerRef = useRef(null);
  const initialLoadRef = useRef(true);

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  /* ---------------- FETCH MESSAGES ---------------- */
  const fetchMessages = async (pageNo) => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const res = await api.get(
        `/admin/chat/messages?conversationId=${conversationId}&page=${pageNo}&size=${PAGE_SIZE}`,
      );

      const apiMessages = res?.data?.data || [];

      const formatted = apiMessages.map((msg) => ({
        id: msg.messageId,
        text: msg.message,
        senderName: msg.senderName || msg.senderType,
        senderImage: msg.senderImage,
        isRight: msg.senderType !== "STUDENT",
        mediaUrls: Array.isArray(msg.mediaUrls) ? msg.mediaUrls : [],
        sentAt: new Date(msg.sentAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      }));

      setMessages((prev) => [...formatted.reverse(), ...prev]);
      setHasMore(!res?.data?.last);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    fetchMessages(1);
  }, [conversationId]);

  /* ---------------- AUTO SCROLL (ONLY FIRST LOAD) ---------------- */
  useLayoutEffect(() => {
    if (!initialLoadRef.current) return;
    if (!containerRef.current || messages.length === 0) return;

    // Delay to allow images/videos to load
    const timeout = setTimeout(() => {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      initialLoadRef.current = false;
    }, 100); // <-- this delay is CRITICAL

    return () => clearTimeout(timeout);
  }, [messages]);

  /* ---------------- SCROLL UP PAGINATION ---------------- */
  const handleScroll = () => {
    if (!containerRef.current || loading || !hasMore) return;

    if (containerRef.current.scrollTop === 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMessages(nextPage);
    }
  };

  /* ---------------- FILE RENDER ---------------- */
  const renderMedia = (url, index) => {
    const ext = url.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
      return (
        <img
          key={index}
          src={url}
          alt=""
          className="img-fluid rounded mb-2"
          style={{ maxHeight: 200 }}
          onLoad={() => {
            if (initialLoadRef.current && containerRef.current) {
              containerRef.current.scrollTop =
                containerRef.current.scrollHeight;
            }
          }}
        />
      );
    }

    if (["mp4", "mov", "avi", "mkv"].includes(ext)) {
      return (
        <video
          key={index}
          controls
          className="w-100 rounded mb-2"
          onLoadedMetadata={() => {
            if (initialLoadRef.current && containerRef.current) {
              containerRef.current.scrollTop =
                containerRef.current.scrollHeight;
            }
          }}
        >
          <source src={url} type={`video/${ext}`} />
        </video>
      );
    }

    return (
      <div key={index} className="d-flex align-items-center gap-2 mb-2">
        <img
          src={documentImg}
          alt="file"
          width={40}
          onLoad={() => {
            if (initialLoadRef.current && containerRef.current) {
              containerRef.current.scrollTop =
                containerRef.current.scrollHeight;
            }
          }}
        />
        <a href={url} download>
          <LuDownload size={22} color="#e60504" />
        </a>
      </div>
    );
  };

  return (
    <section className="chat-section">
      {/* Breadcrumb */}
      <ol className="breadcrumb my-3 px-2">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/othermessaging" className="custom-breadcrumb">
            Other Messages
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">Conversation</li>
      </ol>

      <div className="card mx-3">
        <div className="d-flex justify-content-between p-2 bg-light">
          <span>Conversation View</span>
          <Link to="/othermessaging">
            <button className="btn btn-sm btn-border">Back</button>
          </Link>
        </div>

        <div
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            height: "500px",
            overflowY: "auto",
            padding: "16px",
            background: "#fff",
          }}
        >
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`d-flex mb-4 ${
                msg.isRight ? "justify-content-end" : "justify-content-start"
              }`}
            >
              {!msg.isRight && (
                <img
                  src={
                    msg.senderImage ||
                    "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png"
                  }
                  alt=""
                  className="rounded-circle me-2"
                  width={36}
                  height={36}
                  style={{
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                  onLoad={() => {
                    if (initialLoadRef.current && containerRef.current) {
                      containerRef.current.scrollTop =
                        containerRef.current.scrollHeight;
                    }
                  }}
                />
              )}

              <div
                style={{
                  maxWidth: "70%",
                  background: msg.isRight ? "#dcf8c6" : "#f1f5f9",
                  borderRadius: "12px",
                  padding: "10px 12px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                <div className="mb-1" style={{ fontSize: "xx-small" }}>
                  {msg.senderName}
                </div>

                {msg.text && (
                  <div className="mb-2" style={{ fontSize: "14px" }}>
                    {msg.text}
                  </div>
                )}

                {msg.mediaUrls.map(renderMedia)}

                <div
                  style={{
                    fontSize: "11px",
                    textAlign: "right",
                    color: "#777",
                  }}
                >
                  {msg.sentAt}
                </div>
              </div>

              {msg.isRight && (
                <img
                  src={
                    msg.senderImage ||
                    "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png"
                  }
                  alt=""
                  className="rounded-circle ms-2"
                  width={36}
                  height={36}
                  style={{
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                  onLoad={() => {
                    if (initialLoadRef.current && containerRef.current) {
                      containerRef.current.scrollTop =
                        containerRef.current.scrollHeight;
                    }
                  }}
                />
              )}
            </div>
          ))}

          {loading && <div className="text-center text-muted">Loading...</div>}
        </div>
      </div>
    </section>
  );
}

export default OtherMessagesView;
